import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryKey, UseQueryOptions } from '@tanstack/react-query';
import { useWorkspace } from '../lib/workspace';
import { invalidationMap } from '../lib/query-keys';

/**
 * Workspace-scoped query hook. Injects the workspace slug into the query
 * key so cache entries are isolated per workspace. Returns a shape
 * compatible with the legacy `useAsync` interface (`data`, `error`,
 * `loading`, `refetch`) for incremental migration.
 *
 * Usage:
 * ```ts
 * const { data, loading, error, refetch } = useWorkspaceQuery(
 *   (slug) => queryKeys.findings(slug),
 *   (slug) => listFindings(slug),
 * );
 * ```
 */
export function useWorkspaceQuery<T>(
  keyFn: (slug: string) => QueryKey,
  fetchFn: (slug: string) => Promise<T>,
  options?: Omit<UseQueryOptions<T, Error>, 'queryKey' | 'queryFn'>,
) {
  const { workspace } = useWorkspace();
  const slug = workspace?.slug ?? '';

  const query = useQuery<T, Error>({
    queryKey: keyFn(slug),
    queryFn: () => fetchFn(slug),
    enabled: !!slug,
    ...options,
  });

  return {
    data: query.data ?? null,
    error: query.error?.message ?? null,
    loading: query.isLoading,
    refetch: query.refetch,
    query,
  };
}

/**
 * Workspace-scoped mutation hook with automatic cache invalidation.
 *
 * Usage:
 * ```ts
 * const { mutate, isPending } = useWorkspaceMutation(
 *   (slug, body) => createFinding(slug, body),
 *   'finding:changed',
 * );
 * ```
 */
export function useWorkspaceMutation<TInput, TOutput = unknown>(
  mutationFn: (slug: string, input: TInput) => Promise<TOutput>,
  invalidates?: keyof typeof invalidationMap,
) {
  const { workspace } = useWorkspace();
  const slug = workspace?.slug ?? '';
  const queryClient = useQueryClient();

  return useMutation<TOutput, Error, TInput>({
    mutationFn: (input) => mutationFn(slug, input),
    onSuccess: () => {
      if (invalidates && slug) {
        const keys = invalidationMap[invalidates](slug);
        for (const key of keys) {
          queryClient.invalidateQueries({ queryKey: key });
        }
      }
    },
  });
}
